/**
 * Client-side ordering for sequential opponent/bot attack-initiated messages.
 * Queues attack-initiated when a prior opponent swing has not yet received attack-complete,
 * so slim attack-initiated patches cannot display before the authoritative board update.
 *
 * Human player own attacks (yourTurn + non-bot) bypass the queue entirely.
 */

/** Align with server animation-complete auto-apply timeout (5s). */
export const ATTACK_INITIATED_ORDERING_FLUSH_MS = 5000;

/**
 * @param {object} msg - attack-initiated WS payload
 * @returns {boolean}
 */
export function isPlayerOwnAttackInitiated(msg) {
  if (!msg || typeof msg !== 'object') {
    throw new Error('isPlayerOwnAttackInitiated: msg is required');
  }
  return msg.isBotAttack !== true && msg.yourTurn === true;
}

/**
 * @param {object} msg - attack-initiated WS payload
 * @returns {boolean}
 */
export function isOpponentAttackInitiated(msg) {
  if (!msg || typeof msg !== 'object') {
    throw new Error('isOpponentAttackInitiated: msg is required');
  }
  return msg.isBotAttack === true || msg.yourTurn === false;
}

/**
 * @param {object} msg
 * @param {string|null|undefined} openAttackId
 * @param {Set<string>} completedAttackIds
 * @returns {boolean}
 */
export function shouldQueueOpponentAttackInitiated(msg, openAttackId, completedAttackIds) {
  if (!msg?.attackId) {
    return false;
  }
  if (isPlayerOwnAttackInitiated(msg)) {
    return false;
  }
  if (!isOpponentAttackInitiated(msg)) {
    return false;
  }
  if (!openAttackId) {
    return false;
  }
  if (openAttackId === msg.attackId) {
    return false;
  }
  if (!completedAttackIds || typeof completedAttackIds.has !== 'function') {
    throw new Error('shouldQueueOpponentAttackInitiated: completedAttackIds Set is required');
  }
  if (completedAttackIds.has(openAttackId)) {
    return false;
  }
  return true;
}
